import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, MessageCircle, Send, Trash2, Plus, Play, Search, X, Trophy, Edit2, Check, MoreVertical, Flag, Compass, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { socialAPI, playOfWeekAPI, BACKEND_URL } from '../services/api';
import { useToast } from '../hooks/use-toast';
import WeeklyCountdown from '../components/WeeklyCountdown';
import { format, parseISO } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SimpleVideoPlayer from '../components/SimpleVideoPlayer';
import ExpandableText from '../components/ExpandableText';
import ConfirmDialog from '../components/ConfirmDialog';

// Profile Search Component
const ProfileSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const searchProfiles = useCallback(async (q) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    try {
      setLoading(true);
      const response = await socialAPI.searchProfiles(q);
      setResults(response.data);
    } catch (error) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query) searchProfiles(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchProfiles]);

  const handleSelect = (result) => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    if (result.type === 'team') {
      navigate(`/teams/${result.id}/profile`);
    } else {
      navigate(`/profile/${result.username}`);
    }
  };

  return (
    <div className="relative mb-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          placeholder={t('feed.searchPlaceholder')}
          className="pl-10 pr-10 bg-[#2a2a2b] border-gray-700 text-white h-10"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); setShowResults(false); }}
            className="absolute right-3 top-0 h-full flex items-center text-gray-400 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showResults && (results.length > 0 || loading) && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a1b] border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-auto">
          {loading ? (
            <div className="p-3 text-gray-400 text-sm">{t('common.searching')}</div>
          ) : (
            results.map((result) => (
              <button
                key={result.id || result.username} // Use ID if available, fallback to username
                onClick={() => handleSelect(result)}
                className="w-full flex items-center gap-3 p-3 hover:bg-[#2a2a2b] text-left"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={result.avatar_url} />
                  <AvatarFallback className="bg-blue-600 text-xs">
                    {(result.display_name || result.username || '').substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-medium">{result.display_name || result.username}</p>
                    {result.is_private && <Lock className="h-3 w-3 text-gray-500" />}
                    {result.type === 'team' && (
                      <span className="text-[10px] bg-blue-900/50 text-blue-300 px-1.5 py-0.5 rounded border border-blue-800">TEAM</span>
                    )}
                  </div>
                  {result.type === 'user' && result.username && (
                    <p className="text-gray-400 text-xs">@{result.username}</p>
                  )}
                  {result.positions?.length > 0 && (
                    <p className="text-gray-500 text-xs">{result.positions.join(', ')}</p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const CreatePostInline = ({ onPostCreated }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() || submitting) return;

    setSubmitting(true);
    try {
      const response = await socialAPI.createPost({ caption: text });
      setText('');
      setIsExpanded(false);
      onPostCreated(response.data);
      toast({ title: t('common.success'), description: t('feed.postCreated') });
    } catch (error) {
      toast({ title: t('common.error'), description: t('feed.postCreateError'), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className={`bg-[#1a1a1b] border-gray-800 mb-4 transition-all duration-200 ${isExpanded ? 'ring-1 ring-blue-500/50' : ''}`}>
      <CardContent className="p-3">
        <div className="flex gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.avatar_url} />
            <AvatarFallback className="bg-blue-600">
              {user?.username?.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <form onSubmit={handleSubmit}>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onFocus={() => setIsExpanded(true)}
                placeholder={t('feed.inputPlaceholder')}
                className={`w-full bg-transparent border-none text-white placeholder-gray-500 focus:ring-0 resize-none transition-all duration-200 ${isExpanded ? 'min-h-[80px]' : 'min-h-[32px] h-[32px] overflow-hidden'}`}
                rows={1}
              />
              {isExpanded && (
                <div className="flex justify-between items-center mt-2 border-t border-gray-800 pt-2 animate-in fade-in slide-in-from-top-1">
                  <div className="flex gap-2">
                    {/* Future: Image/Video upload buttons could go here */}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsExpanded(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 font-bold"
                      disabled={!text.trim() || submitting}
                    >
                      {submitting ? '...' : t('feed.share')}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

import PostCard from '../components/PostCard';

const Feed = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [followingOnly, setFollowingOnly] = useState(false);
  const [reportState, setReportState] = useState({ isOpen: false, postId: null, reason: '' });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, postId: null });
  const observer = useRef();

  const lastPostElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setOffset(prevOffset => prevOffset + 10);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    loadFeed(0, followingOnly);
  }, [followingOnly]);

  useEffect(() => {
    if (offset > 0) {
      loadFeed(offset, followingOnly);
    }
  }, [offset]);

  const loadFeed = async (newOffset = 0, filterFollowing = followingOnly) => {
    try {
      setLoading(true);
      const response = await socialAPI.getFeed(10, newOffset, filterFollowing);

      let feedData = response.data;

      if (newOffset === 0) {
        setPosts(feedData);
      } else {
        setPosts(prev => [...prev, ...feedData]);
      }
      setHasMore(response.data.length === 10);
    } catch (error) {
      setError(error.message || 'Failed to load posts');
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    // Handle mock post locally
    if (postId === 'mock-1') {
      setPosts(posts.map(p =>
        p.id === postId
          ? {
            ...p,
            is_liked: !p.is_liked,
            like_count: p.is_liked ? p.like_count - 1 : p.like_count + 1
          }
          : p
      ));
      return;
    }

    try {
      const response = await socialAPI.toggleLike(postId);
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, is_liked: response.data.liked, like_count: response.data.like_count }
          : p
      ));
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handleVote = async (postId) => {
    try {
      const response = await playOfWeekAPI.vote(postId);
      if (response.data.status === 'success') {
        setPosts(posts.map(p =>
          p.id === postId
            ? { ...p, vote_count: response.data.vote_count, user_can_vote: false }
            : { ...p, user_can_vote: false } // User can only vote once per event
        ));
        toast({ title: t('common.success'), description: t('feed.voteRecorded') });
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('feed.voteError'),
        variant: 'destructive'
      });
    }
  };

  const handleComment = async (postId, text) => {
    // Handle mock post locally
    if (postId === 'mock-1') {
      const newComment = {
        id: `comment-${Date.now()}`,
        username: user?.username || 'admin',
        text: text,
        is_owner: true
      };

      setPosts(posts.map(p =>
        p.id === postId
          ? {
            ...p,
            comment_count: p.comment_count + 1,
            comments: [...(p.comments || []), newComment]
          }
          : p
      ));
      return newComment;
    }

    try {
      const response = await socialAPI.addComment(postId, text);
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, comment_count: p.comment_count + 1 }
          : p
      ));
      return response.data;
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
      return null;
    }
  };

  const handleDeleteComment = async (commentId) => {
    // Handle mock post comments locally
    if (commentId.startsWith('comment-')) {
      setPosts(posts.map(p => {
        if (p.id === 'mock-1') {
          return {
            ...p,
            comments: p.comments.filter(c => c.id !== commentId),
            comment_count: p.comment_count - 1
          };
        }
        return p;
      }));
      return true;
    }

    try {
      await socialAPI.deleteComment(commentId);
      return true;
    } catch (error) {
      toast({ title: t('common.error'), description: 'Failed to delete comment', variant: 'destructive' });
      return false;
    }
  };

  const handleDelete = async (postId) => {
    setDeleteConfirm({ isOpen: true, postId });
  };

  const confirmDelete = async () => {
    const postId = deleteConfirm.postId;
    if (!postId) return;

    try {
      await socialAPI.deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      toast({ title: t('common.success'), description: t('feed.postDeleted') });
    } catch (error) {
      toast({ title: t('common.error'), description: t('feed.postDeleteError'), variant: 'destructive' });
    }
  };

  const handleEdit = async (postId, data) => {
    try {
      const response = await socialAPI.updatePost(postId, data);
      setPosts(posts.map(p => p.id === postId ? response.data : p));
      toast({ title: t('common.success'), description: t('feed.postUpdated') });
      return true;
    } catch (error) {
      toast({ title: t('common.error'), description: t('feed.postUpdateError'), variant: 'destructive' });
      return false;
    }
  };

  const handleFeature = async (postId) => {
    try {
      const response = await socialAPI.toggleFeature(postId);
      setPosts(posts.map(p =>
        p.id === postId ? { ...p, is_featured: response.data.is_featured } : p
      ));
      toast({
        title: t('common.success'),
        description: response.data.is_featured
          ? (t('feed.addedToHighlights') || 'Öne çıkanlara eklendi')
          : (t('feed.removedFromHighlights') || 'Öne çıkanlardan kaldırıldı')
      });
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handleReport = (postId) => {
    setReportState({ isOpen: true, postId, reason: '' });
  };

  const submitReport = async (e) => {
    e.preventDefault();
    if (!reportState.postId) return;

    setReportSubmitting(true);
    try {
      await socialAPI.reportPost(reportState.postId, reportState.reason);
      toast({ title: t('common.success'), description: t('feed.reportSuccess') });
      setReportState({ isOpen: false, postId: null, reason: '' });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('feed.reportError'),
        variant: 'destructive'
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-full w-full relative"
      style={{
        background: `
          radial-gradient(1200px 600px at top left, rgba(29, 78, 216, 0.08), transparent 60%),
          radial-gradient(1000px 500px at top right, rgba(147, 51, 234, 0.05), transparent 60%),
          #0a0a0b
        `
      }}
    >
      <div className="max-w-2xl mx-auto pb-4 relative z-10">
        {/* Header - Sticky & Blurred on Mobile, Static on Desktop */}
        <div className="sticky lg:static top-0 z-40 lg:z-auto backdrop-blur-2xl lg:backdrop-blur-none bg-[#0a0a0b]/70 lg:bg-transparent border-b border-white/[0.05] lg:border-none px-4 py-3 lg:pl-16 lg:pr-3 lg:pt-4 lg:py-3 lg:mb-3 flex justify-between items-center transition-all shadow-xl shadow-black/20">

          {/* Explore / Following Toggle - Premium Segmented Control */}
          <div className="flex w-full lg:w-auto p-1 bg-[#161618] rounded-xl border border-white/[0.04] relative">
            <div
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-blue-600 rounded-lg transition-transform duration-300 ease-out shadow-[0_0_15px_rgba(37,99,235,0.4)] ${followingOnly ? 'translate-x-full' : 'translate-x-0'}`}
              style={{ display: user ? 'block' : 'none' }}
            />
            <button
              onClick={() => setFollowingOnly(false)}
              className={`flex-1 lg:px-6 h-8 text-[13px] rounded-lg relative z-10 transition-all duration-300 flex items-center justify-center gap-2 font-bold uppercase tracking-tight ${!followingOnly || !user ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
            >
              <Compass className={`h-4 w-4 transition-transform duration-300 ${!followingOnly || !user ? 'scale-110' : 'scale-90 opacity-70'}`} />
              <span className="hidden sm:inline">{t('feed.explore', 'Keşfet')}</span>
            </button>
            {user && (
              <button
                onClick={() => setFollowingOnly(true)}
                className={`flex-1 lg:px-6 h-8 text-[13px] rounded-lg relative z-10 transition-all duration-300 flex items-center justify-center gap-2 font-bold uppercase tracking-tight ${followingOnly ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
              >
                <Users className={`h-4 w-4 transition-transform duration-300 ${followingOnly ? 'scale-110' : 'scale-90 opacity-70'}`} />
                <span className="hidden sm:inline">{t('feed.following', 'Takip')}</span>
              </button>
            )}
          </div>

          {/* Search & Plus Actions */}
          <div className="flex items-center gap-3 lg:gap-2 ml-4 lg:ml-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSearch(!showSearch)}
              className={`h-10 w-10 lg:h-8 lg:w-8 transition-all rounded-full ${showSearch ? 'text-blue-500 bg-blue-500/10 scale-110' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              <Search className="h-5 w-5 lg:h-4 lg:w-4" />
            </Button>
            {user && (
              <Link to="/post/create">
                <Button size="icon" className="bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 rounded-full h-11 w-11 lg:h-8 lg:w-8 shadow-lg shadow-blue-900/40 border border-blue-400/20 active:scale-90 transition-transform">
                  <Plus className="h-6 w-6 lg:h-4 lg:w-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div className="lg:p-3 lg:pt-0 pb-16 lg:pb-0">

          {/* Profile Search */}
          {showSearch && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-200 mt-2">
              <ProfileSearch />
            </div>
          )}

          {/* Play of the Week Countdown Section */}

          {/* Weekly Play of the Week Countdown */}
          <WeeklyCountdown />



          {/* Create Post Input - Hidden on Mobile to encourage Floating Plus Button or Header Route */}
          {user && (
            <div className="hidden lg:block">
              <CreatePostInline onPostCreated={(newPost) => setPosts([newPost, ...posts])} />
            </div>
          )}

          {loading && posts.length === 0 ? (
            <div className="text-center text-gray-400 py-10">{t('common.loading', 'Loading...')}</div>
          ) : posts.length === 0 ? (
            <div className="text-center text-gray-400 py-10">
              <Play className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{t('feed.noPosts', 'No highlights yet. Be the first to post!')}</p>
            </div>
          ) : (
            <>
              {posts.map((post, index) => {
                if (posts.length === index + 1) {
                  return (
                    <div ref={lastPostElementRef} key={post.id}>
                      <PostCard
                        post={post}
                        onLike={handleLike}
                        onComment={handleComment}
                        onDeleteComment={handleDeleteComment}
                        onVote={handleVote}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        onReport={handleReport}
                        onFeature={handleFeature}
                      />
                    </div>
                  );
                } else {
                  return (
                    <PostCard
                      key={post.id}
                      post={post}
                      onLike={handleLike}
                      onComment={handleComment}
                      onDeleteComment={handleDeleteComment}
                      onVote={handleVote}
                      onDelete={handleDelete}
                      onEdit={handleEdit}
                      onReport={handleReport}
                      onFeature={handleFeature}
                    />
                  );
                }
              })}
              {error && (
                <div className="text-center text-red-500 py-4">{error}</div>
              )}
              {loading && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
            </>
          )}

          {/* Report Dialog */}
          <Dialog open={reportState.isOpen} onOpenChange={(open) => !open && setReportState(prev => ({ ...prev, isOpen: false }))}>
            <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Gönderiyi Şikayet Et</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Bu gönderiyi neden şikayet etmek istediğinizi lütfen belirtin.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitReport} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="reason" className="text-gray-300">Şikayet Nedeni (Opsiyonel)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Örn: Uygunsuz içerik, spam, taciz..."
                    value={reportState.reason}
                    onChange={(e) => setReportState(prev => ({ ...prev, reason: e.target.value }))}
                    className="bg-[#2a2a2b] border-gray-700 min-h-[100px] text-white"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setReportState(prev => ({ ...prev, isOpen: false }))}
                    className="text-gray-400 hover:text-white"
                  >
                    İptal
                  </Button>
                  <Button
                    type="submit"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    disabled={reportSubmitting}
                  >
                    {reportSubmitting ? 'Gönderiliyor...' : 'Şikayet Et'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, postId: null })}
        onConfirm={confirmDelete}
        title={t('feed.deleteConfirmTitle', 'Gönderiyi Sil?')}
        description={t('feed.deleteConfirm', 'Bu gönderiyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')}
      />
    </div>
  );
};

export default Feed;
